'use client';

import Image from 'next/image';

import { useTranslations } from 'next-intl';

import { useEffect, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import VideoPlayer from '@/app/[locale]/(business)/projects/[slug]/_components/video-player';
import { Button } from '@/app/_components/ui/button';
import { DetailedProject } from '@/domain/project/types';
import parseHtml from '@/lib/html-parse';
import { cn } from '@/lib/utils';

interface StrengthsProps {
  project: Pick<DetailedProject, 'description' | 'strengths' | 'video'>;
}

const Strengths = ({
  project: { video, description, strengths },
}: StrengthsProps) => {
  const t = useTranslations();
  const [isVideoInView, setIsVideoInView] = useState(true);
  const [isFloatingVideoClosed, setIsFloatingVideoClosed] = useState(false);
  const [videoSource, setVideoSource] = useState(video);

  useEffect(() => {
    if (isVideoInView) {
      setIsFloatingVideoClosed(false);
    }
  }, [isVideoInView]);

  useEffect(() => {
    // hook necessary to stop the video when closing the smaller video player
    // we set the video source to undefined, then back to the video prop

    if (!videoSource && video) {
      setVideoSource(video);
    }
  }, [videoSource, video]);

  if (!video && !description && !strengths) {
    return null;
  }

  const resetVideo = () => {
    setIsFloatingVideoClosed(true);
    setVideoSource(undefined);
  };

  return (
    <div
      className="flex scroll-mt-44 flex-col gap-8 md:scroll-mt-32 md:gap-12"
      id="summary"
    >
      <h2 className="text-3xl font-bold">
        {t('pages.project.strengths.title')}
      </h2>
      <div>
        {video && videoSource ? (
          <Waypoint
            onEnter={() => setIsVideoInView(true)}
            onLeave={() => setIsVideoInView(false)}
          >
            <div>
              <div
                className={cn(
                  !isVideoInView && !isFloatingVideoClosed
                    ? 'fixed bottom-2 left-2 z-20 w-64 md:bottom-6 md:left-6 md:w-72'
                    : '',
                )}
              >
                <Button
                  size="sm"
                  cornerStyle="rounded"
                  className={cn(
                    !isVideoInView
                      ? 'absolute -right-7 -top-7 block'
                      : 'hidden',
                  )}
                  onClick={() => resetVideo()}
                >
                  x
                </Button>

                <VideoPlayer video={videoSource} />
              </div>
            </div>
          </Waypoint>
        ) : null}
        {/* Here we add a placeholder in */}
        {video ? (
          <div
            className={cn(
              'hidden',
              !isVideoInView
                ? 'block aspect-video w-full rounded-2xl bg-foreground'
                : '',
            )}
          />
        ) : null}
      </div>
      {description ? (
        <div className="relative w-full rounded-2xl bg-primary-50 p-6 md:p-10">
          <Image
            className="absolute left-3 top-3 size-3 -rotate-90 md:left-6 md:top-6 md:size-4"
            width={16}
            height={16}
            src="/corner.png"
            role="presentation"
            alt=""
          />
          <div
            className="whitespace-pre-wrap text-center text-lg text-primary marker:text-lg marker:text-primary md:text-justify [&>*_a]:text-primary [&>*_a]:underline [&_a]:text-primary [&_a]:underline [&_li_p]:inline [&_ul]:flex [&_ul]:list-inside [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-3"
            data-testid="page-project-description"
          >
            {parseHtml(description)}
          </div>
          <Image
            className="absolute bottom-3 right-3 size-3 rotate-90 md:bottom-6 md:right-6 md:size-4"
            width={16}
            height={16}
            src="/corner.png"
            role="presentation"
            alt=""
          />
        </div>
      ) : null}
      {strengths ? (
        <div className="flex w-full flex-col gap-6 rounded-2xl bg-primary-50 p-6 md:p-10">
          <h3 className="text-justify text-2xl font-bold">
            {t('pages.project.strengths.subtitle')}
          </h3>
          <div className="flex flex-col gap-6 whitespace-pre-wrap marker:text-lg marker:text-primary [&>*_a]:text-primary [&>*_a]:underline [&_a]:text-primary [&_a]:underline [&_li_p]:inline [&_ul]:flex [&_ul]:list-inside [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-3">
            {parseHtml(strengths)}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Strengths;
